import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Send, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fileUrl, fmtDateTime, prezzoLabel } from "@/lib/api";

const ReplyBox = ({ annuncio }) => {
  const navigate = useNavigate();
  const [testo, setTesto] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/annunci/${annuncio.id}/messaggi`, { testo });
      toast.success("Messaggio inviato all'inserzionista");
      navigate(`/messaggi?c=${data.conv_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="bg-slate-50 border rounded-lg p-4 space-y-3" data-testid="annuncio-reply-box">
      <div className="text-xs font-mono-x uppercase tracking-wider text-muted-foreground">Rispondi all'annuncio</div>
      <Textarea rows={3} value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi un messaggio all'inserzionista…" data-testid="annuncio-reply-input" />
      <Button onClick={send} disabled={!testo.trim() || sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="annuncio-reply-send-button">
        <Send className="h-4 w-4 mr-1" />{sending ? "Invio…" : "Invia messaggio"}
      </Button>
    </div>
  );
};

const EmailBox = ({ annuncio }) => {
  const [testo, setTesto] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/annunci/${annuncio.id}/contatta-email`, { testo });
      toast.success("Richiesta inviata via email all'inserzionista");
      setTesto("");
      setSent(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="bg-slate-50 border rounded-lg p-4 space-y-3" data-testid="annuncio-email-box">
      <div className="text-xs font-mono-x uppercase tracking-wider text-muted-foreground">Richiedi informazioni via email</div>
      <p className="text-xs text-muted-foreground">L'inserzionista non è un socio dell'app: riceverà il tuo messaggio via email con il titolo e il testo dell'annuncio, insieme al tuo nome, email e telefono per risponderti.</p>
      {sent && <p className="text-sm text-emerald-700" data-testid="annuncio-email-sent">Richiesta inviata. La risposta arriverà alla tua email.</p>}
      <Textarea rows={3} maxLength={2000} value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi la tua richiesta di informazioni…" data-testid="annuncio-email-input" />
      <Button onClick={send} disabled={!testo.trim() || sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="annuncio-email-send-button">
        <Mail className="h-4 w-4 mr-1" />{sending ? "Invio…" : "Invia richiesta via email"}
      </Button>
    </div>
  );
};

export const AnnuncioDetail = ({ annuncio, onOpenChange }) => {
  const { user } = useAuth();
  if (!annuncio) return null;
  const a = annuncio;
  return (
    <Dialog open={!!a} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="annuncio-detail-dialog">
        <DialogHeader>
          <div className="flex gap-2 mb-1">
            <Badge className={a.tipo === "vendo" ? "bg-emerald-600" : "bg-red-600"}>{a.tipo.toUpperCase()}</Badge>
            <Badge variant="outline">{a.categoria}</Badge>
            {a.categoria_barca && <Badge variant="outline">{a.categoria_barca}</Badge>}
          </div>
          <DialogTitle className="font-heading text-3xl uppercase">{a.titolo}</DialogTitle>
        </DialogHeader>
        {a.foto_ids.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {a.foto_ids.map((id) => <img key={id} src={fileUrl(id)} alt="" className="w-full h-44 object-cover rounded-lg bg-slate-100" />)}
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-3xl font-heading font-bold text-[#0B192C]" data-testid="annuncio-detail-price">{prezzoLabel(a.prezzo)}</span>
          {a.luogo && <span className="flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-4 w-4" />{a.luogo}</span>}
        </div>
        <p className="whitespace-pre-line text-slate-700">{a.descrizione}</p>
        <p className="text-xs text-muted-foreground">Pubblicato il {fmtDateTime(a.created_at)} da {[a.owner_cognome, a.owner_nome].filter(Boolean).join(" ")}</p>
        {a.esterno ? <EmailBox annuncio={a} /> : a.owner_id !== user.id ? <ReplyBox annuncio={a} /> : <p className="text-sm text-muted-foreground">È un tuo annuncio: le risposte arrivano nei tuoi <Link to="/messaggi" className="text-emerald-700 underline">Messaggi</Link>.</p>}
      </DialogContent>
    </Dialog>
  );
};
